import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { useI18n } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

export const Insights = () => {
  const { t } = useI18n();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    fetch(`${API}/posts`)
      .then((r) => r.json())
      .then((d) => setPosts(Array.isArray(d) ? d.slice(0, 3) : []))
      .catch(() => {});
  }, []);

  if (!posts.length) return null;

  return (
    <section id="insights" data-testid="insights-section" className="relative border-t border-white/10 bg-ink-900">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div className="max-w-2xl">
            <Reveal><SectionLabel index="[ • ]">{t("insights.label")}</SectionLabel></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                {t("insights.h1")} <span className="text-aurora">{t("insights.hkw")}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-white/55">{t("insights.sub")}</p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <Link
              to="/blog"
              data-testid="insights-see-all"
              className="group inline-flex items-center gap-2 border border-white/15 px-5 py-3 font-mono text-xs uppercase tracking-[0.15em] text-white/70 transition-colors duration-300 hover:border-cyan-neon hover:text-cyan-neon"
            >
              {t("insights.all")} <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3">
          {posts.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, ease: EASE, delay: i * 0.08 }}
            >
              <Link to={`/blog/${p.slug}`} data-testid={`insights-card-${i}`} className="group block h-full overflow-hidden rounded-2xl border border-white/10 bg-ink-800 transition-colors duration-300 hover:border-cyan-neon/40">
                <div className="relative h-44 overflow-hidden">
                  <img src={p.cover} alt={p.title} className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-90" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-800 to-transparent" />
                  <span className="absolute left-3 top-3 rounded-full border border-cyan-neon/30 bg-ink-900/80 px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest text-cyan-neon backdrop-blur-md">{p.category}</span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-lg font-bold leading-snug text-white transition-colors group-hover:text-cyan-neon">{p.title}</h3>
                  <p className="mt-2 line-clamp-2 font-body text-sm leading-relaxed text-white/55">{p.excerpt}</p>
                  <div className="mt-4 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-white/35">
                    <Clock size={12} /> {p.read_min} {t("blog.read")}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
